/**
 * The ONLY file in the repository that knows the numeric values of `jsonc-parser`
 * (ADR-0022).
 *
 * Do not `import { SyntaxKind } from 'jsonc-parser'`. The library declares `SyntaxKind`,
 * `ScanError` and `ParseErrorCode` as ambient `const enum`s, and under
 * `verbatimModuleSyntax` — which we need so that `import type` can never become a runtime
 * import — that is `TS2748: Cannot access ambient const enums`. The runtime does export
 * them as ordinary objects, so `tokens.test.ts` asserts at run time that the tables below
 * still match. A dependency bump that renumbers anything fails CI instead of silently
 * corrupting repairs.
 *
 * Our member names are deliberately ours: `Whitespace` reads better than jsonc's `Trivia`,
 * and `UnterminatedString` better than `UnexpectedEndOfString`. The test carries the
 * name-to-name mapping.
 */

/** Token kinds produced by the scanner. Numerically equal to `jsonc.SyntaxKind`. */
export const TokenKind = {
  OpenBrace: 1,
  CloseBrace: 2,
  OpenBracket: 3,
  CloseBracket: 4,
  Comma: 5,
  Colon: 6,
  Null: 7,
  True: 8,
  False: 9,
  String: 10,
  Number: 11,
  LineComment: 12,
  BlockComment: 13,
  LineBreak: 14,
  Whitespace: 15,
  Unknown: 16,
  EOF: 17,
} as const;
export type TokenKind = (typeof TokenKind)[keyof typeof TokenKind];

/** Scanner-level errors. `None` is 0. Numerically equal to `jsonc.ScanError`. */
export const ScanErrorKind = {
  None: 0,
  UnterminatedComment: 1,
  UnterminatedString: 2,
  UnterminatedNumber: 3,
  InvalidUnicode: 4,
  InvalidEscape: 5,
  InvalidCharacter: 6,
} as const;
export type ScanErrorKind = (typeof ScanErrorKind)[keyof typeof ScanErrorKind];

/**
 * Parser-level errors, as reported by `parseTree`. Numerically equal to
 * `jsonc.ParseErrorCode`.
 *
 * These are never shown to a user: `jsonc`'s error stream is a poor user surface (three
 * errors for one typo in `{a:1}`, a trailing comma reported on the following brace), which
 * is exactly why `checks/structure.ts` translates them into our own codes and collapses
 * the cascades (ADR-0007).
 */
export const ParseErrorKind = {
  InvalidSymbol: 1,
  InvalidNumberFormat: 2,
  PropertyNameExpected: 3,
  ValueExpected: 4,
  ColonExpected: 5,
  CommaExpected: 6,
  CloseBraceExpected: 7,
  CloseBracketExpected: 8,
  EndOfFileExpected: 9,
  InvalidCommentToken: 10,
  UnexpectedEndOfComment: 11,
  UnexpectedEndOfString: 12,
  UnexpectedEndOfNumber: 13,
  InvalidUnicode: 14,
  InvalidEscapeCharacter: 15,
  InvalidCharacter: 16,
} as const;
export type ParseErrorKind = (typeof ParseErrorKind)[keyof typeof ParseErrorKind];

/**
 * One token, with half-open offsets in UTF-16 code units.
 *
 * Trivia is kept: repair rules need to see comments and whitespace to reason about what
 * follows what, and the fidelity printer needs to know what it is allowed to regenerate
 * (ADR-0011, ADR-0013).
 */
export interface Token {
  readonly kind: TokenKind;
  readonly from: number;
  readonly to: number;
  /** `ScanErrorKind.None` (0) when the token scanned cleanly. */
  readonly scanError: ScanErrorKind;
}

/**
 * A `parseTree` error, as data. Produced by `json/parse.ts`, consumed by
 * `lint/checks/structure.ts`, and never rendered directly.
 */
export interface RawParseError {
  readonly kind: ParseErrorKind;
  readonly offset: number;
  readonly length: number;
}

/** Comments, line breaks and whitespace: present in the stream, absent from the grammar. */
export function isTrivia(token: Token): boolean {
  return (
    token.kind === TokenKind.LineComment ||
    token.kind === TokenKind.BlockComment ||
    token.kind === TokenKind.LineBreak ||
    token.kind === TokenKind.Whitespace
  );
}
